import {
  HomeOutlined,
  NotificationsOutlined,
  ErrorOutlined,
  MenuOutlined,
  LoginOutlined,
  LogoutOutlined,
  FlightOutlined,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  useMediaQuery,
  useTheme,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  PaymentOutlined,
  AccountBalance,
  LocalShipping,
  MarkEmailUnread,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { useDeliveryTracking } from "../../hooks/useDeliveryTracking";
import { collection, query, where, getDocs, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

const ModernNavBar = ({ colorMode, setColorMode }) => {
  const [auth, setAuth] = useState(false);
  const [logCount, setLogCount] = useState(0);
  const [detailedNotifications, setDetailedNotifications] = useState([]);
  const [notificationEl, setNotificationEl] = useState(null);
  const [deliveryEl, setDeliveryEl] = useState(null);
  const { user, logout } = UserAuth();
  const notifications = useNotifications();
  const { deliveries, pendingCount } = useDeliveryTracking(user);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    user ? setAuth(true) : setAuth(false);
  }, [user]);

  // Fetch log count for error badge - only for authenticated users
  useEffect(() => {
    if (!user || !auth) {
      setLogCount(0);
      return;
    }

    const fetchLogCount = async () => {
      try {
        const q = query(collection(db, 'logs'), where('status', '!=', 'completed'));
        const querySnapshot = await getDocs(q);
        setLogCount(querySnapshot.size);
      } catch (error) {
        console.error("Error fetching log count:", error);
        try {
          await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            message: error.message,
            reference: 'Error fetching log count',
            page: 'ModernNavBar',
            status: 'pending'
          });
        } catch (logError) {
          console.error("Error logging to Firestore:", logError);
        }
      }
    };

    fetchLogCount();
    // Set up a real-time listener for log updates
    const unsubscribe = onSnapshot(
      query(collection(db, 'logs'), where('status', '!=', 'completed')),
      (snapshot) => {
        setLogCount(snapshot.size);
      },
      (error) => {
        console.error("Error in log count listener:", error);
      }
    );

    return () => unsubscribe();
  }, [user, auth]);

  // Helper function to get date from various field types
  const getDateFromField = (dateField) => {
    if (!dateField) return null;
    
    if (dateField.toDate) {
      return dateField.toDate();
    }
    
    if (typeof dateField === 'string') {
      return new Date(dateField);
    }
    
    if (dateField instanceof Date) {
      return dateField;
    }
    
    return null;
  };

  // Fetch detailed notifications for the dropdown
  useEffect(() => {
    if (!user || !auth) {
      setDetailedNotifications([]);
      return;
    }

    const fetchDetailedNotifications = async () => {
      try {
        const today = new Date();
        const notifs = [];
        
        // Fetch payables due
        const payablesSnap = await getDocs(query(
          collection(db, 'payables'),
          where('status', '==', 'Open')
        ));
        payablesSnap.forEach(doc => {
          const data = doc.data();
          const dueDate = getDateFromField(data.dueDate);
          if (dueDate && dueDate <= today) {
            notifs.push({
              id: doc.id,
              type: 'payable',
              title: `Payable Due: $${data.totalAmount}`,
              description: `Due date: ${dueDate.toLocaleDateString()}`,
              icon: <PaymentOutlined color="error" />
            }); 
          }
        });
        
        // Fetch receivables due
        const receivablesSnap = await getDocs(query(
          collection(db, 'receivable'),
          where('status', '==', 'Open')
        ));
        receivablesSnap.forEach(doc => {
          const data = doc.data();
          const dueDate = getDateFromField(data.dueDate);
          if (dueDate && dueDate <= today) {
            notifs.push({
              id: doc.id,
              type: 'receivable',
              title: `Receivable Due: $${data.totalAmount}`,
              description: `Due date: ${dueDate.toLocaleDateString()}`,
              icon: <AccountBalance color="warning" />
            });
          }
        });

        // Fetch deliveries due
        const deliveriesSnap = await getDocs(query(
          collection(db, 'po'),
          where('status', '==', 'Pending')
        ));

        deliveriesSnap.forEach(doc => {
          const poData = doc.data();
          const poNumber = poData.poNumber || '';
          
          if (poNumber.length >= 6) {
            const dateStr = poNumber.substring(0, 6);
            const month = parseInt(dateStr.substring(0, 2)) - 1;
            const day = parseInt(dateStr.substring(2, 4));
            const year = 2000 + parseInt(dateStr.substring(4, 6));
            
            const poDate = new Date(year, month, day);
            const dueDate = new Date(poDate);
            dueDate.setDate(dueDate.getDate() + 30);
            
            if (dueDate <= today) {
              notifs.push({
                id: doc.id,
                type: 'delivery',
                title: `PO Due: ${poNumber}`,
                description: `Due date: ${dueDate.toLocaleDateString()}`,
                icon: <LocalShipping color="info" />
              });
            }
          }
        });

        // Fetch unread inbox messages
        const inboxSnap = await getDocs(query(
          collection(db, 'inbox'),
          where('read', '==', false)
        ));
        inboxSnap.forEach(doc => {
          const data = doc.data();
          const title = `Message: ${data.name || data.email || 'Unknown'}`;
          const preview = (data.message || '').slice(0, 80) + ((data.message || '').length > 80 ? '…' : '');
          notifs.push({
            id: doc.id,
            type: 'inbox',
            title,
            description: preview,
            icon: <MarkEmailUnread color="primary" />
          });
        });

        setDetailedNotifications(notifs);
        
      } catch (error) {
        console.error('Error fetching detailed notifications:', error);
        try {
          await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            message: error.message,
            reference: 'Error fetching detailed notifications:',
            page: 'ModernNavBar',
            status: 'pending'
          });
        } catch (logError) {
          console.error("Error logging to Firestore:", logError);
        }
      }
    };

    fetchDetailedNotifications();
    // Set up interval to check periodically
    const interval = setInterval(fetchDetailedNotifications, 300000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [user, auth]);

  // Notification handlers
  const handleNotificationClick = (event) => {
    setNotificationEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationEl(null);
  };

  // Delivery dropdown handlers
  const handleDeliveryClick = (event) => {
    setDeliveryEl(event.currentTarget);
  };

  const handleDeliveryClose = () => {
    setDeliveryEl(null);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          boxShadow: 1
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuOutlined />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Prime
                </Typography>
              </Box>
            </Link>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {auth && (
              <>
                <IconButton
                  onClick={() => navigate('/main')}
                  title="Home"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <HomeOutlined />
                </IconButton>
                
                <IconButton
                  onClick={handleNotificationClick}
                  title="Notifications"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <Badge badgeContent={notifications?.total || 0} color="error">
                    <NotificationsOutlined />
                  </Badge>
                </IconButton>

                <IconButton
                  onClick={handleDeliveryClick}
                  title="Package Tracking"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <Badge 
                    badgeContent={pendingCount || 0} 
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#ffd700',
                        color: '#000',
                        fontWeight: 'bold'
                      }
                    }}
                  >
                    <FlightOutlined />
                  </Badge>
                </IconButton>

                <IconButton
                  onClick={() => navigate('/log')}
                  title="Error Logs"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <Badge badgeContent={logCount} color="error">
                    <ErrorOutlined />
                  </Badge>
                </IconButton>

                <IconButton
                  onClick={handleLogout}
                  title="Logout"
                  size={isMobile ? 'small' : 'medium'}
                >
                  <LogoutOutlined />
                </IconButton>
              </>
            )}

            {!auth && (
              <IconButton
                onClick={() => navigate('/login')}
                title="Login"
                size={isMobile ? 'small' : 'medium'}
              >
                <LoginOutlined />
              </IconButton>
            )}

            {/* Theme toggle hidden for now */}
            {/* <IconButton
              onClick={() => setColorMode(!colorMode)}
              title={`Switch to ${colorMode ? 'Light' : 'Dark'} Mode`}
              size={isMobile ? 'small' : 'medium'}
            >
              {colorMode ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton> */}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notification Dropdown */}
      <Popover
        open={Boolean(notificationEl)}
        anchorEl={notificationEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 400,
            overflow: 'auto',
          }
        }}
      >
        <List>
          {detailedNotifications.length === 0 ? (
            <ListItem>
              <ListItemText primary="No notifications" />
            </ListItem>
          ) : (
            detailedNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem 
                  button
                  onClick={() => {
                    handleNotificationClose();
                    navigate(`/${notification.type}`);
                  }}
                >
                  <ListItemIcon>
                    {notification.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={notification.title}
                    secondary={notification.description}
                  />
                </ListItem>
                {index < detailedNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Popover>

      {/* Delivery Tracking Dropdown */}
      <Popover
        open={Boolean(deliveryEl)}
        anchorEl={deliveryEl}
        onClose={handleDeliveryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 450,
            overflow: 'auto',
            backgroundColor: theme.palette.mode === 'dark' ? '#2d3748' : '#ffffff',
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlightOutlined />
            Package Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingCount} pending deliveries
          </Typography>
        </Box>
        
        <List sx={{ p: 0 }}>
          {deliveries.length === 0 ? (
            <ListItem sx={{ textAlign: 'center', py: 3 }}>
              <ListItemText 
                primary="No pending deliveries" 
                secondary="All packages have been delivered"
              />
            </ListItem>
          ) : (
            deliveries.map((delivery, index) => {
              // Get carrier-specific colors
              const getCarrierColor = (carrier) => {
                const colorMap = {
                  'DHL': '#ffd700',
                  'UPS': '#8b4513',
                  'USPS': '#1976d2',
                  'FedEx': '#663399',
                  'Amazon': '#ff9500',
                  'Apple': '#333333',
                  'Other': '#666666'
                };
                return colorMap[carrier] || colorMap.Other;
              };

              const carrierColor = getCarrierColor(delivery.carrier);
              
              return (
                <React.Fragment key={delivery.id}>
                  <ListItem 
                    button
                    onClick={() => {
                      handleDeliveryClose();
                      navigate('/tracking');
                    }}
                    sx={{
                      py: 2,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#4a5568' : '#f5f5f5'
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                          {delivery.title}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          backgroundColor: delivery.isOverdue ? '#ff5722' : delivery.isToday ? '#4caf50' : delivery.isSoon ? '#ff9800' : '#2196f3',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          {delivery.daysLeft === 0 ? '0' : Math.abs(delivery.daysLeft)}
                          <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                            DAYS
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {delivery.isOverdue ? 'Overdue' : delivery.isToday ? 'Delivered by Today' : `Expected Delivery by ${delivery.formattedEta}`}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          backgroundColor: carrierColor,
                          color: delivery.carrier === 'DHL' ? '#000' : '#fff',
                          px: 1,
                          py: 0.3,
                          borderRadius: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          {delivery.carrier}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {delivery.notes && `${delivery.notes.substring(0, 30)}${delivery.notes.length > 30 ? '...' : ''}`}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < deliveries.length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          )}
        </List>
        
        {deliveries.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { textDecoration: 'underline' } 
                }}
                onClick={() => {
                  handleDeliveryClose();
                  navigate('/tracking');
                }}
              >
                View Online
              </Typography>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default ModernNavBar;
